import { useDispatch, useSelector } from 'react-redux'
import './ItemView.css'
import React, { useEffect, useState, useRef } from 'react'
import { AiOutlineBarChart, AiOutlineCheck } from 'react-icons/ai'
import { TbCompass, TbFaceIdError, TbReplace } from 'react-icons/tb'
import { BiFilterAlt, BiPen } from 'react-icons/bi'
import { VscTypeHierarchy } from 'react-icons/vsc'
import { HiArrowsRightLeft } from 'react-icons/hi2'
import { AiOutlineLoading } from 'react-icons/ai';
import { fetchModalUser } from '../../../../actions/ModalAction'
import { generateItemTypeIcon, generateItemTypeText } from '../../../../tools/itemTypeConversion'
import { Tabs } from '@mantine/core'
import DatumList from '../Datum/DatumList'
import { ImLink } from 'react-icons/im'
import RelationalItem from './RelationalItem'
import { getRelationItem } from '../../../../api/RelationalItemManipulation';
import SuggestedItem from './SuggestedItem'
import { MdArrowForwardIos } from 'react-icons/md'
import { generateRelationIcon } from '../../../../tools/generateRelationIcon';
import { getUser } from '../../../../api/UserRequest';
import _ from 'lodash';
import { unstable_batchedUpdates } from 'react-dom';

const ItemView = ( {data} ) => {

const {user} = useSelector((state) => state.authReducer.authData)
const dispatch = useDispatch();
const [activeTab, setActiveTab] = useState("info_data")
const [activeTool, setActiveTool] = useState(null);
const [riTools, setRiTools] = useState(null);
const [relaItems, setRelaItems] = useState([]);
const [originalRelaItems, setOriginalRelaItems] = useState([]);
const [relationFilter, setRelationFilter] = useState([]);
const [loading, setLoading] = useState(true);
const [count, setCount] = useState([]);
const [relationalAlign, setRelationalAlign] = useState(0);
const [relationalConflict, setRelationalConflict] = useState(0);
const [totalAlignTriggers, setTotalAlignTriggers] = useState(0);
const [totalConflictTriggers, setTotalConflictTriggers] = useState(0);
const [suggestions, setSuggestedItems] = useState([])
const hasFetchedRef = useRef(false);
const prevDataIdRef = useRef(null);
  
useEffect(()=>{
  // Makes sure the active tab is the info tab for better experience
  setActiveTab("info_data");
}, [data])

useEffect(() => {
  if (!data) {
    setLoading(false);
    return;
  }

  const currentId = data._id?.toString();

  if (hasFetchedRef.current && prevDataIdRef.current === currentId) {
      return;
  }

  hasFetchedRef.current = true;
  prevDataIdRef.current = currentId;
  const fetchRelationalItem = async () => {
    setLoading(true);

    // Prepare encoded log details for audit or logging purposes
    try {
      const logDetails = {
        activity: "fetch_item",
        category: 'interaction',
        userId: user._id,
        sessionId: localStorage.getItem('sessionId'),
        data: {
          userId: user._id,
          username: user.username,
        }
       
      };
      const encodedLogDetails = encodeURIComponent(JSON.stringify(logDetails));

      // Counters to track total alignment and conflict triggers from relational items
      let align = 0;
      let conflict = 0;

      // Helper to separate suggested items from relational ones
      const processItems = (items) => {
        const suggestions = [];
        const filtered = [];

        for (const item of items) {
          if (item.transferrence >= 0) {
            if(user.userType === 0) {
              align += item.gitemAlignTriggers;
              conflict += item.gitemConflictTriggers;
            }
            if (user.userType === 2) {
                align += item.itemOwnAlignTriggers;
                conflict += item.itemOwnConflictTriggers;
            }
            filtered.push(item);
          } else {
            suggestions.push(item);
          }
        }

        return { filtered, suggestions };
      };

      // Helper to retrieve data of users the current user follows (for general users only)
      const getGroupData = async () => {
        if (!Array.isArray(user.follow)) return [];

        const groupPromises = user.follow.map((id) =>
          getUser(id, encodedLogDetails).catch((e) =>
            console.error(`Failed to get user ${id}:`, e)
          )
        );
        return (await Promise.all(groupPromises)).filter(Boolean);
      };

      const isGeneralUser = user.userType === 0;
      const groups = isGeneralUser ? await getGroupData() : null;

      // Main API call to retrieve relational item data
      const mainResponse = await getRelationItem(
        data.relationalData,
        data._id,
        groups,
        user.settings.relations,
        user.settings.scope,
        user._id,
        encodedLogDetails
      );

      const items = mainResponse.data.relationalItems;
      const counts = mainResponse.data.relationCounts;

      // Process items into relational and suggested categories, update counters
      const { filtered, suggestions } = processItems(items);
      unstable_batchedUpdates(() => {
        setRelationalAlign(align);
        setRelationalConflict(conflict);
        setTotalAlignTriggers(
          align + data.itemTriggers.infoAlignTriggers + data.itemTriggers.relationalAlignTriggers
        );
        setTotalConflictTriggers(
          conflict + data.itemTriggers.infoConflictTriggers + data.itemTriggers.relationalConflictTriggers
        );
        setCount(counts);
        setSuggestedItems(suggestions);

        // Compare with existing data to prevent dispatch loop
        // if (
        //   !_.isEqual(filtered, data.relationalItems) ||
        //   !_.isEqual(suggestions, data.suggestions)
        // ) {
        //   dispatch({
        //     type: "MODAL_SET_RELATIONAL_DATA",
        //     payload: {
        //       relationalItems: filtered,
        //       suggestions,
        //     },
        //   });
        // }

        setRelaItems(filtered);
        setOriginalRelaItems(filtered);
      });

      // If no relational items exist, reset the relational trigger counts
      if (filtered.length === 0) {
        setRelationalAlign(0);
        setRelationalConflict(0);
        setTotalAlignTriggers(
          prev => prev - align
        );
        setTotalConflictTriggers(
          prev => prev - conflict
        );
      }
    } catch (error) {
      console.error("Error Fetching Relational Item: ", error);
    } finally {
      setLoading(false);
    }
  };

  if (data) {
    fetchRelationalItem();
  } else {
    setLoading(false);
  }
}, [data]);

  const createdDate = () => {
      const date = new Date(data.createdAt);
      return (date.toDateString());
  }

  const countReferences = () => {
    var count = 0;
    // I think I need to come back here and correct - it was throwing for group type 
    // for not having a stampDetails array
    if (user.userType === 0) {
      for (let d = 0; d < data.infoData.length; d++) {
        count += data.infoData[d].stampDetails.length;
      }
      for (let d = 0; d < data.relationalData.length; d++) {
        count += data.relationalData[d].stampDetails.length;
      }
    }
    return count;
  }

  const adoptionValue = (() => {
    if (data.adoptions.findIndex(i=>i===user._id) > -1) {
      return data.adoptions.findIndex(i=>i===user._id) + 1;
    } else {
      return null;
    }
  })()
  
  const parentItemObj = {
    parent_itemId: data._id,
    parent_itemName: data.itemName
  }

  const loadUser = () => {
    const logDetails = {
      activity: "load_user",
      category: 'interaction',
      data: {
        userId: user._id,
        username: user.username,
        ...parentItemObj
      },
      userId: user._id,
      sessionId: localStorage.getItem('sessionId')
    }
    dispatch(fetchModalUser(data.userId, logDetails))
  }

  const clearRiFilters = () => {
    setRelationFilter([]);
    document.getElementById("iv_relation_dropdown").selectedIndex = 0;
  }

  // Does this need to be separated by tab, or can I make a general function?
  const generateRiTools = (tool) => {
    if (activeTool === tool) {
      console.log(`Closing ${tool}`);
      setRiTools(null);
      handleExpandTools("ri");
      setActiveTool(null);
    } else {
      setActiveTool(tool);
      switch (tool) {
        case "info":
          console.log("Firing info case");
          setRiTools(
            <>
              <div className='iv-th-text'>
                Every Item that is "related" to this Item is displayed here.  For more information about how two Items are related, click on the {<AiOutlineBarChart />} icon next to the Item to view all of the Data that describe their relationship.<br/><br/>
                Use the {<BiFilterAlt />} tool to Filter the Items by the type of relationship.
              </div>
              <div onClick={()=>generateRiTools("close")} className='iv-th-arrow-container'>
                <MdArrowForwardIos className='General-exp-arrow' />

              </div>
            </>
          )
          if (document.getElementById("iv_ri_tools").classList.contains("General-hidden")) {
            handleExpandTools("ri");
          } 
          break;
        case "filter":
          console.log("firing filter case");
          setRiTools(
            <>
              <div className='iv-th-text'>
                Sort and Filter Options:
              </div>
              <div className='iv-th-filter-container'>
                <select id="iv_relation_dropdown" class="iv-relation-dropdown" onChange={(e)=>toggleRelationFilter(e.target.value)}>
                  <option value="" disabled hidden>Filter by Relation:</option>
                  {count.map(r => {
                    if (r.count > 0) {
                      return <option value={r.text}>{r.text}</option>
                    }
                  })}   
                </select>
                <button onClick={()=>clearRiFilters()}>Clear</button>
              </div>
              <div onClick={()=>generateRiTools("close")} className='iv-th-arrow-container'>
                <MdArrowForwardIos className='General-exp-arrow' />

              </div>
            </>
          );
          if (document.getElementById("iv_ri_tools").classList.contains("General-hidden")) {
            handleExpandTools("ri");
          } 
          break;
        case "close":
          console.log("firing close");
          setRiTools(null);
          handleExpandTools("ri");
      }
    }
  }

  const handleExpandTools = (t) => {
    document.getElementById("iv_" + t + "_tools").classList.toggle("General-hidden");
  }
  const toggleRelationFilter = (filter) => {
    console.log(relationFilter)
    setRelationFilter((prev) =>
    prev.length === 1 && prev[0] === filter
    ? []
    : [filter]);
  };

  useEffect(() => {
    // No filters → reset
    if (relationFilter.length === 0) {
      setRelaItems(
        [...originalRelaItems].sort(
          (a, b) => b.relatedData.length - a.relatedData.length
        )
      );
      return;
    }
  
    // Apply filters
    const itemsFiltered = originalRelaItems.filter(item =>
      item.relations.some(r => relationFilter.includes(r))
    );
  
    const dataFiltered = itemsFiltered.map(item => ({
      ...item,
      relatedData: item.relatedData.filter(datum =>
        datum.relations.some(rel => {
          const item1Match =
            rel.ref1 === item.itemId || rel.ref1 === data._id;
          const item2Match =
            rel.ref2 === item.itemId || rel.ref2 === data._id;
  
          return (
            item1Match &&
            item2Match &&
            relationFilter.includes(rel.text)
          );
        })
      )
    }));
  
    setRelaItems(dataFiltered);
  }, [relationFilter, originalRelaItems]);
  

  function NoData( {tab} ) {
    var noItemsText = "";
    const itemType = generateItemTypeText(data.itemType);
    const tabText = () => {
      switch(tab) {
        case "info":
          return "any Info Data for";
        case "relational":
          return "any Relational Data for";
        case "suggestion":
          return `any Data containing ${itemType}s to suggest for`;
        default:
          return "";
      }
    };
    const tabTextGeneral = () => {
      switch(tab) {
        case "info":
          return "any information describing this Item";
        case "relational":
          return "any information describing the relationship between any other Items and";
        case "relationalItems":
          return "any related Items";
        case "suggestion":
          return `any ${itemType}s to suggest for`;
        default:
          return "";
      }
    }
    
    switch(user.userType) {
        case 0:
            {tab === "relationalItems" ? 
            noItemsText = `Your Groups have not found any other Items that have a relationship with this ${itemType} yet.`    
            : noItemsText = `Your Groups have not found ${tabTextGeneral()} this ${itemType} yet.`}   
            break;
        case 1:
            {tab === "relationalItems" ?
            noItemsText = `You have not created any Data which show another Item having a relationship with this ${itemType} yet.`
            : noItemsText = `You have not created ${tabText()} this ${itemType} yet.`
            }
            break;
        case 2:
            {tab === "relationalItems" ?
            noItemsText = `You have not stamped any Data which show another Item having a relationship with this ${itemType} yet.`
            : noItemsText = `You have not stamped ${tabText()} this ${itemType} yet.`
            }
            break;
    }
    return (
        <div className='General-nr-container'>
            <TbFaceIdError size='30'/>
            <div className='General-nr-hint' data-testid={`no-data-${tab}`}>{noItemsText}</div>
        
        </div>
    )
  }

  return (
        <div 
            className="iv-item-container"
            // Eventually the class will depend on if item is triggered.
        >
            <div className="datumBody">
                <div className="iv-top-bar">
                    <span className='iv-name FlexColumn'>
                        {data.itemName}
                        <img src={process.env.REACT_APP_PUBLIC_FOLDER + data.image} class="iv-item-image"></img>
                    </span>
                </div>
                <div className="iv-stats FlexRow">
                {user.userType !== 1 
                  ? (
                      loading 
                        ? <div className="spinner-container">
                            <AiOutlineLoading size="1rem" className="spin-animation" />
                          </div>
                        : <div className='flex'>
                            <div className={totalAlignTriggers === 0 && totalConflictTriggers === 0 ? "FlexRow iv-status-clear" : ""}>
                              {totalAlignTriggers === 0 && totalConflictTriggers === 0
                              ?
                              <>
                                <AiOutlineCheck size="1rem" />
                              </>
                                : ''
                              }
                            </div>
                            <div className={totalAlignTriggers > 0 ? "FlexRow iv-status-align" : ""}>
                              {totalAlignTriggers > 0
                              ?
                              <>
                                    <TbCompass size="1rem" />
                                    <span className="">{totalAlignTriggers}</span>
                                  </>
                                : ''
                              }
                            </div>
                            <div className={totalConflictTriggers > 0 ? "FlexRow iv-status-conflict" : ""}>
                              {totalConflictTriggers > 0
                              ?
                              <>
                                    <TbCompass size="1rem" />
                                    <span className="">{totalConflictTriggers}</span>
                                  </>
                                : ''
                              }
                            </div>
                          </div>
                      
                    )
                  : ''
                }
                    <div className="iv-specific-stats FlexRow">
                      <AiOutlineBarChart size="1rem" />
                      <span className="referenced">{data.infoData.length + data.relationalData.length}</span>
                      {user.userType === 1 && adoptionValue 
                        ? 
                          <>
                            <ImLink/>
                            <span className="referenced">{adoptionValue}</span>
                          </>
                        : ""
                      }
                    </div>
                    <div className="iv-overall-stats FlexRow">
                      <VscTypeHierarchy size="1rem" />
                      <span className="referenced">{data.links.length}</span>
                      <ImLink/>
                      <span className="referenced">{data.adoptions.length}</span>
                    </div>
                    {generateItemTypeIcon(data.itemType)}
                </div>
            </div>

            <hr/>


            <Tabs radius="md" defaultValue="info_data" value={activeTab} onTabChange={setActiveTab}>
              <Tabs.List grow>
                <Tabs.Tab value="info_data" icon={
                  <span className='iv-tabWithNotification'><AiOutlineBarChart size="1rem" />
                    {data.itemTriggers.infoAlignTriggers > 0 && <span className='iv-align-count'>{data.itemTriggers.infoAlignTriggers}</span>}
                    {data.itemTriggers.infoConflictTriggers > 0 && <span className='iv-trigger-count'>{data.itemTriggers.infoConflictTriggers}</span>}
                  </span>}
                ></Tabs.Tab>
                <Tabs.Tab value="relational_data" icon={
                  <span className='iv-tabWithNotification'><HiArrowsRightLeft size="1rem" /><AiOutlineBarChart size="1rem" />
                  {data.itemTriggers.relationalAlignTriggers > 0  && <span className='iv-align-count'>{data.itemTriggers.relationalAlignTriggers}</span>}
                  {data.itemTriggers.relationalConflictTriggers > 0 && <span className='iv-trigger-count'>{data.itemTriggers.relationalConflictTriggers}</span>}
                  </span>}
                ></Tabs.Tab>
                <Tabs.Tab 
                  value="relational_items" 
                  icon={
                    loading ? (
                      <span className="iv-tabWithNotification">
                        <span className="loading-icon">
                          <AiOutlineLoading size="1rem" className="spin-animation" />
                        </span>
                      </span>
                    ) : (
                      <span className="iv-tabWithNotification">
                        <HiArrowsRightLeft size="1rem" />
                        <VscTypeHierarchy size="1rem" />
                          <div className='flex'>
                            {relationalAlign > 0  && <span className='iv-align-count'>{relationalAlign}</span>}
                            {relationalConflict > 0  && <span className='iv-trigger-count'>{relationalConflict}</span>}
                            
                          </div>
                      </span>
                    )
                  }
                ></Tabs.Tab>
                <Tabs.Tab value="suggestions" icon={
                  loading 
                    ? <span className="iv-tabWithNotification">
                        <span className="loading-icon">
                          <AiOutlineLoading size="1rem" className="spin-animation" />
                        </span>
                      </span>
                      : <span className="iv-tabWithNotification">
                          <TbReplace />
                          {suggestions.length > 0 && (
                            <span className="iv-suggestions-count">{suggestions.length}</span>
                          )}
                      </span>
                  }
                >
                </Tabs.Tab>
                <Tabs.Tab value="author" icon={<BiPen size="1rem" />}></Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="info_data" pt="xs">
                <div className="iv-data-container">
                  {data.infoData.length > 0 ? data.infoData.map((datum, _id) => {
                    return <DatumList data={datum} key={_id} triggerType={Array.isArray(datum.triggerTypes) && datum.triggerTypes.length > 0 ? datum.triggerTypes[0] : null}  />
                  })
                  : <NoData tab={"info"} />
                  }
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="relational_data" pt="xs">
                <div className="iv-data-container">
                  {data.relationalData.length > 0 ? data.relationalData.map((datum, _id) => {
                    return <DatumList data={datum} key={_id} triggerType={Array.isArray(datum.triggerTypes) && datum.triggerTypes.length > 0 ? datum.triggerTypes[0] : null}  />
                  })
                  : <NoData tab={"relational"} />
                  }
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="relational_items" pt="xs">                
                <div className="iv-data-row">
                  
                  {relaItems.length > 0 && count && 
                    <div className="iv-relation-icon-menu">
                      {count.map((c, index) => {
                        if (c.count > 0) {
                          return (
                            <span
                              key={index}
                              title={c.text}
                              className={`iv-relation-button ${
                                relationFilter.length === 0 || relationFilter.includes(c.text) ? "pressed" : ""
                              }`}
                              onClick={() => toggleRelationFilter(c.text)}
                            >
                              <span className="iv-relation-icon">{generateRelationIcon(c.text)}</span>
                              
                            </span>
                          )
                        }
                        })}
                    </div>
                  }
                
                  <div className="iv-data-container">
                    {loading ? (
                      "Loading Relational Items..."
                    ) : relaItems.length > 0 ? (
                      relaItems.map((item) => (
                        <RelationalItem item={item} key={item.itemId} parentItemObj={parentItemObj} />
                      ))
                    ) : relationFilter.length > 0 ? (
                      "An error occurred. This should not happen"
                    ) : (
                      <NoData tab={"relationalItems"} />
                    )}
                  </div>
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="suggestions" pt="xs">
                <div className="iv-data-container">
                  { loading ? (
                    "Loading Suggestions..."
                  ) :
                  suggestions.length > 0 ? suggestions.map((item, id) => {
                    return <SuggestedItem item={item} key={item.itemId} parentItemObj={parentItemObj} />
                  })
                  : <NoData tab={"suggestion"} />
                  }
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="author" pt="xs">
                <div className="bottom-bar FlexRow">
                  <div className="datumDetails">
                    <div className="generalBar authorBar" onClick={loadUser}>
                        <BiPen/>
                        
                        <img src={process.env.REACT_APP_PUBLIC_FOLDER + data.authorImage} alt="" className='authorBarImg' />
                        <span className="datumAuthor">
                            @{data.authorName}
                        </span>
                        <span className="timestamp">{createdDate()}</span>
                    </div>
                  </div>
                </div>
              </Tabs.Panel>
            </Tabs>
      </div>        




  )
}

export default ItemView